import clsx from 'clsx';

export interface StepperProps {
  steps: string[];
  activeIndex: number;
  onStepClick?: (index: number) => void;
  className?: string;
}

/**
 * NEXA-style stepper: a gradient tile sequence where the active tile is
 * filled ink-solid, completed tiles are deep blue, and upcoming tiles fade
 * out to whisper-blue. Playfair numerals are gone; step numbers are
 * Inter display, tabular.
 */
export function Stepper({ steps, activeIndex, onStepClick, className }: StepperProps) {
  const total = steps.length;
  const safeIndex = Math.min(Math.max(activeIndex, 0), total - 1);
  const activeLabel = steps[safeIndex];

  return (
    <nav aria-label="Form progress" className={clsx('w-full', className)}>
      {/* Mobile — compact line + label */}
      <div className="md:hidden">
        <div className="flex items-center gap-2 mb-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.125em] text-ink-subtle">
            &gt; STEP
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.125em] text-ink">
            {String(safeIndex + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
          </span>
        </div>
        <TileRow total={total} activeIndex={safeIndex} />
        <p className="mt-4 font-display font-medium text-[clamp(22px,3vw,28px)] leading-tight">
          {activeLabel}
        </p>
      </div>

      {/* Desktop — labeled tile sequence */}
      <div className="hidden md:block">
        <div className="flex items-center gap-4 mb-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.125em] text-ink-subtle">
            &gt; STEP {String(safeIndex + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
          </span>
        </div>
        <TileRow total={total} activeIndex={safeIndex} />
        <ol className="mt-4 grid grid-cols-4 gap-3">
          {steps.map((label, i) => {
            const isActive = i === safeIndex;
            const isComplete = i < safeIndex;
            const clickable = Boolean(onStepClick) && i <= safeIndex;
            const content = (
              <div className="flex flex-col gap-1">
                <span
                  className={clsx(
                    'font-mono text-[10px] uppercase tracking-[0.125em]',
                    isActive
                      ? 'text-ink'
                      : isComplete
                        ? 'text-blue-deep'
                        : 'text-ink-subtle',
                  )}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span
                  className={clsx(
                    'text-[0.9375rem] font-display',
                    isActive
                      ? 'text-ink font-medium'
                      : isComplete
                        ? 'text-ink-muted'
                        : 'text-ink-subtle',
                  )}
                >
                  {label}
                </span>
              </div>
            );

            return (
              <li
                key={label}
                className="text-left"
                aria-current={isActive ? 'step' : undefined}
              >
                {clickable ? (
                  <button
                    type="button"
                    onClick={() => onStepClick?.(i)}
                    className="w-full text-left hover:opacity-80 transition-opacity"
                  >
                    {content}
                  </button>
                ) : (
                  <div>{content}</div>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}

function TileRow({ total, activeIndex }: { total: number; activeIndex: number }) {
  return (
    <div className="flex items-stretch gap-1.5 w-full">
      {Array.from({ length: total }).map((_, i) => {
        const isActive = i === activeIndex;
        const isComplete = i < activeIndex;
        const t = total === 1 ? 0 : i / (total - 1);
        const color = interpolateBlue(t);
        let bg: string;
        if (isActive) bg = '#0B0D12';
        else if (isComplete) bg = '#1D4ED8';
        else bg = color;

        return (
          <div
            key={i}
            className="flex-1 rounded-md transition-colors duration-300"
            style={{ height: 10, backgroundColor: bg, opacity: isComplete ? 1 : isActive ? 1 : 0.85 }}
            aria-hidden="true"
          />
        );
      })}
    </div>
  );
}

function interpolateBlue(t: number): string {
  const r = Math.round(29 + (219 - 29) * t);
  const g = Math.round(78 + (234 - 78) * t);
  const b = Math.round(216 + (254 - 216) * t);
  return `rgb(${r}, ${g}, ${b})`;
}
